import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import { storage } from "@/lib/storage";
import {
  HttpResponse,
  PingType,
  QuoMessage,
  QuoMessageDirection,
  QuoMessageResponse,
  QuoMessageStatus,
  SendQuoMessageRequest,
} from "@/types";

export function useSendQuoMessage() {
  const queryClient = useQueryClient();
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();

  return useMutation({
    mutationFn: ({
      sendQuoMessageRequest,
    }: {
      sendQuoMessageRequest: SendQuoMessageRequest;
    }) =>
      guacHttpClient.post<QuoMessage, HttpResponse<QuoMessage>>(
        "quo-messages",
        sendQuoMessageRequest,
      ),
    // Called right before the mutation is executed. For optimistic updates.
    onMutate: async ({
      sendQuoMessageRequest,
    }: {
      sendQuoMessageRequest: SendQuoMessageRequest;
    }) => {
      const queryKey = ["quo-messages", sendQuoMessageRequest.talentId ?? null];

      // Cancel any existing queries for the same query key in order to prevent optimistic updates from being overwritten.
      await queryClient.cancelQueries({ queryKey });

      // Create a new optimistic message.
      const currentUser = storage.getUser();
      const now = new Date().toISOString();
      const optimisticMessage: QuoMessageResponse = {
        id: `optimistic-${crypto.randomUUID()}`,
        talentId: sendQuoMessageRequest.talentId ?? null,
        userId: currentUser?.id ?? 0, // TODO: Maybe throw an error if the user is not found?
        quoId: "",
        direction: QuoMessageDirection.OUTGOING,
        body: sendQuoMessageRequest.body,
        media: null,
        status: QuoMessageStatus.QUEUED, // IMPORTANT: Queued status enables loading indicator in the UI.
        sentAt: now,
        sentByAgent: false,
        createdAt: now,
        updatedAt: now,
        user: {
          firstName: currentUser?.firstName ?? "", // TODO: Maybe throw an error if the user is not found?
          lastName: currentUser?.lastName ?? "", // TODO: Maybe throw an error if the user is not found?
        },
      };

      // Add the optimistic message to the cached data.
      queryClient.setQueryData<QuoMessageResponse[]>(queryKey, (old = []) => [
        optimisticMessage,
        ...old,
      ]);

      return { optimisticId: optimisticMessage.id, queryKey };
    },
    onSuccess: (response, variables, context) => {
      const currentUser = storage.getUser();
      const realMessage: QuoMessageResponse = {
        ...response.data,
        user: {
          firstName: currentUser?.firstName ?? "",
          lastName: currentUser?.lastName ?? "",
        },
      };

      // Replace the optimistic message with the real record.
      queryClient.setQueryData<QuoMessageResponse[]>(
        context.queryKey,
        (old = []) => {
          const found = old.some((m) => m.id === context.optimisticId);
          if (found) {
            return old.map((m) =>
              m.id === context.optimisticId ? realMessage : m,
            );
          }
          return [realMessage, ...old];
        },
      );

      const candidacyId = variables.sendQuoMessageRequest.candidacyId;
      if (candidacyId) {
        const lastPing = {
          at: response.data.sentAt,
          direction: response.data.direction,
          type: PingType.MESSAGE,
          status: response.data.status,
          stafferId: response.data.userId,
          sentByAgent: response.data.sentByAgent,
          body: response.data.body,
        };
        setCandidacyInAllLists({
          candidacyId,
          newLastPing: lastPing,
        });
        setCandidacyInfo({
          candidacyId,
          lastCommunication: lastPing,
          ...(response.data.sentByAgent
            ? { lastAiCommunication: lastPing }
            : { lastHumanCommunication: lastPing }),
        });
      }
    },
    onError: (_error, _variables, context) => {
      if (!context) return;

      queryClient.setQueryData<QuoMessageResponse[]>(
        context.queryKey,
        (old = []) =>
          old.map((m) =>
            m.id === context.optimisticId
              ? { ...m, status: QuoMessageStatus.UNDELIVERED }
              : m,
          ),
      );
    },
  });
}
